import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './layout';
import { initializeStore, shouldUseStoreKit, verifyWithBackend } from './utils/iosPurchases';
import { getApiUrl } from './utils';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { setRefreshToken } from './utils/secureStorage';
import Home from './pages/Home';
// ARCHIVED: import Explore from './pages/Explore';
import Saved from './pages/Saved';
// ARCHIVED: Collections feature not yet implemented
// import Collections from './pages/Collections';
// import CollectionDetail from './pages/CollectionDetail';
import StylePicks from './pages/StylePicks';
// ARCHIVED: import AIStylist from './pages/AIStylist';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
// ARCHIVED: import VisualSearch from './pages/VisualSearch';
import MyWardrobe from './pages/MyWardrobe';
import StyleStudio from './pages/StyleStudio';
import Account from './pages/Account';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import News from './pages/News';
import ContentStudio from './pages/ContentStudio';
import AIStudio from './pages/AIStudio';
import Promoters from './pages/Promoters';
import Lookbooks from './pages/Lookbooks';
import OutfitCalendar from './pages/OutfitCalendar';
import AdminPublish from './pages/AdminPublish';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Support from './pages/Support';
import { Toaster } from './components/ui/toaster';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  // Initialize StoreKit on iOS native
  useEffect(() => {
    if (shouldUseStoreKit()) {
      const API_URL = getApiUrl();
      initializeStore(async (transaction) => {
        await verifyWithBackend(transaction, API_URL);
      });
    }
  }, []);

  // Handle deep links (Google OAuth callback on iOS)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleAppUrlOpen = async (event) => {
        const url = event.url;
        console.log('📱 Deep link received:', url);

        // Handle Google OAuth callback: fashiondisc://google-callback?token=...&user=...
        if (url.startsWith('fashiondisc://google-callback')) {
          try {
            // Close the Safari View Controller
            await Browser.close();
          } catch (e) {
            // Browser might already be closed
          }

          const params = new URL(url.replace('fashiondisc://', 'https://placeholder/'));
          const token = params.searchParams.get('token');
          const refreshToken = params.searchParams.get('refreshToken');
          const userData = params.searchParams.get('user');
          const error = params.searchParams.get('error');

          if (error) {
            console.error('❌ Google OAuth error:', error);
            // Navigate to login with error
            window.location.href = '/login?google_error=' + encodeURIComponent(error);
            return;
          }

          if (token && userData) {
            try {
              const user = JSON.parse(decodeURIComponent(userData));
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(user));
              await setRefreshToken(refreshToken);
              console.log('✅ Google Sign-In successful via OAuth redirect');
              // Navigate to home and reload to update auth state
              window.location.href = '/home';
            } catch (parseErr) {
              console.error('❌ Failed to parse OAuth callback data:', parseErr);
              window.location.href = '/login?google_error=' + encodeURIComponent('Sign-in failed');
            }
          }
        }
      };

      CapApp.addListener('appUrlOpen', handleAppUrlOpen);

      return () => {
        CapApp.removeAllListeners();
      };
    }
  }, []);

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/my-wardrobe" replace />} />
          <Route path="/home" element={
            <Layout currentPageName="Home">
              <Home />
            </Layout>
          } />
          {/* ARCHIVED: Explore tab
          <Route path="/explore" element={
            <Layout currentPageName="Explore">
              <Explore />
            </Layout>
          } />
          */}
          <Route path="/saved" element={
            <Layout currentPageName="Saved">
              <Saved />
            </Layout>
          } />
          {/* ARCHIVED: Collections feature — backend not implemented yet
          <Route path="/collections" element={
            <Layout currentPageName="Collections">
              <Collections />
            </Layout>
          } />
          <Route path="/collections/:id" element={
            <Layout currentPageName="Collections">
              <CollectionDetail />
            </Layout>
          } />
          */}
          <Route path="/style-picks" element={
            <Layout currentPageName="StylePicks">
              <StylePicks />
            </Layout>
          } />
          {/* ARCHIVED: AI Stylist route — orphaned page, removed
          <Route path="/ai-stylist" element={
            <Layout currentPageName="AI Stylist">
              <AIStylist />
            </Layout>
          } />
          */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* ARCHIVED: Visual Search tab
          <Route path="/visual-search" element={
            <Layout currentPageName="VisualSearch">
              <VisualSearch />
            </Layout>
          } />
          */}
          <Route path="/my-wardrobe" element={
            <Layout currentPageName="MyWardrobe">
              <MyWardrobe />
            </Layout>
          } />
          <Route path="/style-studio" element={
            <Layout currentPageName="StyleStudio">
              <StyleStudio />
            </Layout>
          } />
          <Route path="/account" element={
            <Layout currentPageName="Account">
              <Account />
            </Layout>
          } />
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
          <Route path="/news" element={
            <Layout currentPageName="News">
              <News />
            </Layout>
          } />
          <Route path="/content-studio" element={
            <Layout currentPageName="ContentStudio">
              <ContentStudio />
            </Layout>
          } />
          <Route path="/ai-studio" element={
            <Layout currentPageName="AIStudio">
              <AIStudio />
            </Layout>
          } />
          <Route path="/promoters" element={
            <Layout currentPageName="Promoters">
              <Promoters />
            </Layout>
          } />
          <Route path="/lookbooks" element={
            <Layout currentPageName="Lookbooks">
              <Lookbooks />
            </Layout>
          } />
          <Route path="/outfit-calendar" element={
            <Layout currentPageName="OutfitCalendar">
              <OutfitCalendar />
            </Layout>
          } />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/support" element={<Support />} />
          {/* Hidden admin page — not linked anywhere in navigation */}
          <Route path="/admin/publish" element={<AdminPublish />} />
        </Routes>
        <Toaster />
      </Router>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
